/**
 * GFM serializers for the table nodes, feeding the clipboard's
 * `text/plain` half.
 *
 * Why we own this at all: `tiptap-markdown` ships a `table` serializer
 * that refuses any table it can't express in strict GFM — a body-cell
 * first row, a merged cell, or a cell holding more than one block — and
 * falls back to a node that writes the literal string `[table]` when the
 * `html` option is off (it is; raw HTML is not an authoring surface
 * here). It also ships NO serializer for `tableRow` / `tableCell` /
 * `tableHeader` at all, so a partial cell selection — whose slice is a
 * bare fragment of rows with no `table` wrapper — copied as
 * `[tableRow]`. Since the first row of a pasted table is almost never a
 * header, in practice most tables were un-copyable as text.
 *
 * Nothing here touches persistence: bodies are TipTap JSON, and markdown
 * is a projection. The matching projections on the server
 * (`lib/richtext/markdown-projection.ts`, `wiki/export.ts`) follow the
 * same rules, so the same document reads identically whether it was
 * copied out of the editor or exported.
 *
 * The rules, in one place:
 *   - Row 0 is the header, promoted even when its cells are body cells.
 *     GFM has no header-less table, and an empty header row renders as a
 *     blank bold band; promoting matches both server projections and the
 *     spreadsheet convention the data usually came from.
 *   - A cell's blocks are joined with `<br>`, the only line break GFM
 *     honours inside a cell.
 *   - A merged cell writes its content once and blank-fills the columns
 *     it spans, so every row keeps the table's full width and the
 *     delimiter row stays honest.
 */

import type { Node as PMNode } from '@tiptap/pm/model'

/**
 * The slice of `prosemirror-markdown`'s `MarkdownSerializerState` we
 * rely on, plus `tiptap-markdown`'s `inTable` addition. Declared
 * structurally rather than imported: `tiptap-markdown` doesn't export
 * its subclass, and the fields below are the stable surface its own
 * bundled serializers use.
 */
export interface MarkdownState {
  nodes: Record<
    string,
    (state: MarkdownState, node: PMNode, parent: unknown, index: number) => void
  >
  marks: Record<string, unknown>
  options: Record<string, unknown>
  out: string
  inTable: boolean
  write(content: string): void
  ensureNewLine(): void
  closeBlock(node: PMNode): void
  renderInline(parent: PMNode, fromBlockStart?: boolean): void
  text(text: string, shouldEscape?: boolean): void
}

/** `parent` reaches a serializer as either a Node or a bare Fragment. */
interface ParentLike {
  childCount: number
}

/**
 * ProseMirror nodes expose an iteration callback and `child(i)`, not an
 * iterator, so materialise the children once and walk the array.
 */
function childrenOf(node: PMNode): PMNode[] {
  const children: PMNode[] = []
  for (let index = 0; index < node.childCount; index += 1) children.push(node.child(index))
  return children
}

/**
 * Spans arrive unvalidated: the server's rich-text validator is a node-type
 * whitelist and checks no attributes, so a stored `colspan` of a billion
 * round-trips through save and load. Clamping matters most HERE, because this
 * code runs synchronously inside a copy handler on the main thread — an
 * unbounded span would allocate per column and hang the tab on Cmd+C. The cap
 * matches the one both server-side renderers use.
 */
const MAX_CELL_SPAN = 1000

const positiveInt = (value: unknown): number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? Math.min(value, MAX_CELL_SPAN)
    : 1

/**
 * A sub-state for rendering one cell, sharing the outer state's node and
 * mark serializers except for `hardBreak`.
 *
 * A hard break is the one node whose correct spelling depends on where
 * it sits: `\` + newline everywhere else, `<br>` inside a table, because
 * a newline would end the row. `tiptap-markdown` knows this and routes a
 * hard break in a table to its HTML fallback, which with `html: false`
 * writes `[hardBreak]` into the middle of the row. Swapping the node
 * serializer for the duration of a cell keeps the outer document's hard
 * breaks untouched.
 */
function createCellState(state: MarkdownState): MarkdownState {
  const Ctor = state.constructor as new (
    nodes: MarkdownState['nodes'],
    marks: MarkdownState['marks'],
    options: MarkdownState['options'],
  ) => MarkdownState
  const sub = new Ctor(
    { ...state.nodes, hardBreak: (s) => s.write('<br>') },
    state.marks,
    // Copied, not shared: prosemirror-markdown's constructor WRITES defaults
    // into this object (`tightLists`, `hardBreakNodeName`).
    { ...state.options },
  )
  sub.inTable = true
  return sub
}

/** Render a block's INLINE content through the real inline pipeline. */
function renderInlineToString(state: MarkdownState, node: PMNode): string {
  const saved = state.out
  state.out = ''
  try {
    state.renderInline(node)
    return state.out
  } finally {
    // A throw from any mark serializer would otherwise abort the whole
    // clipboard serialization with the scratch buffer still installed.
    state.out = saved
  }
}

/**
 * Wrap text in a code span with a fence long enough to survive any
 * backticks inside it, per CommonMark's rule.
 */
function inlineCode(text: string): string {
  if (!text) return ''
  const longestRun = (text.match(/`+/g) ?? []).reduce((max, run) => Math.max(max, run.length), 0)
  const fence = '`'.repeat(longestRun + 1)
  // CommonMark strips one leading AND trailing space from a span that has
  // both, so an edge space needs its own pair just as an edge backtick does.
  const pad = /^[`\s]|[`\s]$/.test(text) ? ' ' : ''
  return `${fence}${pad}${text}${pad}${fence}`
}

/**
 * Depth cap for the recursive walks below (quotes, callouts, expand sections
 * and nested lists all recurse). Bodies reach this code unvalidated for depth,
 * and a stack overflow inside a synchronous copy handler has nothing above it
 * to catch. Mirrors the server-side renderers' cap.
 */
const MAX_CELL_DEPTH = 128

const LIST_TYPES = new Set(['bulletList', 'orderedList', 'taskList'])

/**
 * Callout panels. GFM has no callout, and a quote is the closest shape both
 * server-side projections already degrade them to — so a panel reads the same
 * whether the document was copied, exported, or projected for an agent.
 */
const PANEL_TYPES = new Set([
  'infoPanel',
  'notePanel',
  'successPanel',
  'warningPanel',
  'errorPanel',
  'customPanel',
])

const stringAttr = (node: PMNode, key: string): string =>
  typeof node.attrs?.[key] === 'string' ? (node.attrs[key] as string) : ''

/** Render a block's children to one line each, dropping the empty ones. */
function renderChildBlocks(state: MarkdownState, block: PMNode, depth: number): string[] {
  if (depth > MAX_CELL_DEPTH) return []
  const lines: string[] = []
  for (const child of childrenOf(block)) {
    const text = renderCellBlock(state, child, depth + 1)
    if (text) lines.push(text)
  }
  return lines
}

/** Children as a quote — the closest GFM shape for a quote or a callout. */
function renderQuotedChildren(state: MarkdownState, block: PMNode, depth: number): string {
  return renderChildBlocks(state, block, depth)
    .map((line) => `> ${line}`)
    .join('<br>')
}

/**
 * Flatten a list to one line per item. A GFM cell can't hold a real
 * list, so the markers are kept as text and the items are joined by the
 * caller with `<br>` — the shape a reader recognises even though the
 * renderer won't build a `<ul>` from it.
 */
function renderListLines(state: MarkdownState, list: PMNode, depth: number): string[] {
  if (depth > MAX_CELL_DEPTH) return []
  const lines: string[] = []
  const indent = '  '.repeat(depth)
  const start = positiveInt(list.attrs?.start)
  let ordinal = list.type.name === 'orderedList' ? start : 1

  for (const item of childrenOf(list)) {
    let marker = '- '
    if (list.type.name === 'orderedList') {
      marker = `${ordinal}. `
      ordinal += 1
    } else if (list.type.name === 'taskList') {
      marker = item.attrs?.checked === true ? '- [x] ' : '- [ ] '
    }

    let wroteFirst = false
    for (const child of childrenOf(item)) {
      if (LIST_TYPES.has(child.type.name)) {
        lines.push(...renderListLines(state, child, depth + 1))
        continue
      }
      const text = renderCellBlock(state, child, depth + 1)
      if (!text) continue
      lines.push(wroteFirst ? `${indent}  ${text}` : `${indent}${marker}${text}`)
      wroteFirst = true
    }
    if (!wroteFirst) lines.push(`${indent}${marker}`.trimEnd())
  }

  return lines
}

/**
 * Render one block of a cell to a single line of GFM-safe text.
 *
 * Deliberately never delegates to the block-level serializers: those
 * close blocks and emit blank lines, both of which end a table row.
 */
function renderCellBlock(state: MarkdownState, block: PMNode, depth = 0): string {
  if (depth > MAX_CELL_DEPTH) return ''
  switch (block.type.name) {
    case 'paragraph':
      return renderInlineToString(state, block)
    case 'heading': {
      // `#` inside a cell is literal text to a GFM renderer, so the
      // emphasis is carried by bold instead of being silently dropped.
      const text = renderInlineToString(state, block)
      return text ? `**${text}**` : ''
    }
    case 'bulletList':
    case 'orderedList':
    case 'taskList':
      return renderListLines(state, block, 0).join('<br>')
    case 'codeBlock':
      return block.textContent.split('\n').map(inlineCode).filter(Boolean).join('<br>')
    case 'blockquote':
      return renderQuotedChildren(state, block, depth)
    case 'horizontalRule':
      // `---` on its own line inside a cell would be read as the table's own
      // delimiter row; the HTML spelling is the only one that survives.
      return '<hr>'
    case 'expandSection': {
      // The summary is a label for the body, not a quote around it, so the
      // body renders plain and only the label carries emphasis.
      const summary = stringAttr(block, 'summary')
      const body = renderChildBlocks(state, block, depth).join('<br>')
      if (!summary) return body
      return body ? `**${summary}**<br>${body}` : `**${summary}**`
    }
    case 'videoEmbed':
      return stringAttr(block, 'caption')
    case 'image': {
      const src = typeof block.attrs?.src === 'string' ? block.attrs.src : ''
      const alt = typeof block.attrs?.alt === 'string' ? block.attrs.alt : ''
      return src ? `![${alt}](${src})` : ''
    }
    default: {
      if (PANEL_TYPES.has(block.type.name)) return renderQuotedChildren(state, block, depth)
      // A node with block children would render as nonsense through the
      // inline pipeline, so fall back to its text rather than guessing.
      const text = block.inlineContent ? renderInlineToString(state, block) : ''
      return text || block.textContent
    }
  }
}

/**
 * Render a whole cell to the text between two pipes.
 *
 * The newline collapse is the backstop: any block renderer that still
 * emits one (a nested structure, a future node type) would otherwise
 * break the row in half and destroy the table for every reader.
 */
export function renderCellToGfm(state: MarkdownState, cell: PMNode): string {
  const sub = createCellState(state)
  const parts: string[] = []
  for (const block of childrenOf(cell)) {
    const rendered = renderCellBlock(sub, block)
    if (rendered.trim()) parts.push(rendered)
  }
  return parts
    .join('<br>')
    .replace(/[ \t]*\n+/g, '<br>')
    .replace(/\|/g, '\\|')
    .trim()
}

const rowLine = (cells: readonly string[]): string => `| ${cells.join(' | ')} |`

const delimiterLine = (width: number): string =>
  `| ${Array.from({ length: width }, () => '---').join(' | ')} |`

/**
 * Lay the table out as a dense grid of rendered cells, resolving
 * `colspan` and `rowspan` into the blank columns they occupy so every
 * emitted row has the same width as the delimiter row.
 */
function buildGrid(state: MarkdownState, table: PMNode): string[][] {
  const rows = childrenOf(table).filter((row) => row.type.name === 'tableRow')
  if (rows.length === 0) return []

  const grid: string[][] = rows.map(() => [])
  const taken: boolean[][] = rows.map(() => [])

  for (const [rowIndex, row] of rows.entries()) {
    let column = 0
    for (const cell of childrenOf(row)) {
      while (taken[rowIndex]?.[column]) column += 1
      const text = renderCellToGfm(state, cell)
      const colspan = positiveInt(cell.attrs?.colspan)
      const rowspan = positiveInt(cell.attrs?.rowspan)
      for (let dr = 0; dr < rowspan; dr += 1) {
        const targetRow = grid[rowIndex + dr]
        const targetTaken = taken[rowIndex + dr]
        if (!targetRow || !targetTaken) continue
        for (let dc = 0; dc < colspan; dc += 1) {
          targetRow[column + dc] = dr === 0 && dc === 0 ? text : ''
          targetTaken[column + dc] = true
        }
      }
      column += colspan
    }
  }

  const width = grid.reduce((max, row) => Math.max(max, row.length), 0)
  return grid.map((row) => Array.from({ length: width }, (_, index) => row[index] ?? ''))
}

/** `table` — the whole-table path. */
export function serializeTable(state: MarkdownState, node: PMNode): void {
  const grid = buildGrid(state, node)
  const width = grid[0]?.length ?? 0
  if (width === 0) {
    state.closeBlock(node)
    return
  }
  for (const [index, row] of grid.entries()) {
    state.write(rowLine(row))
    state.ensureNewLine()
    if (index === 0) {
      state.write(delimiterLine(width))
      state.ensureNewLine()
    }
  }
  state.closeBlock(node)
}

/**
 * `tableRow` — the partial-selection path. A cell selection's slice is a
 * bare fragment of rows, so the first selected row becomes the header
 * for the same reason row 0 does in a whole table.
 */
// Known limitation: this path expands `colspan` but does no `rowspan`
// accounting, because a bare row fragment has no siblings to resolve a carry
// against. A partial selection spanning a merged cell can therefore emit a
// body row narrower than its delimiter. The whole-table path resolves it
// correctly through the occupancy grid; merged cells are reachable only by
// paste today, so this stays a documented gap rather than a second grid.
export function serializeTableRow(
  state: MarkdownState,
  node: PMNode,
  parent: unknown,
  index: number,
): void {
  const cells: string[] = []
  for (const cell of childrenOf(node)) {
    cells.push(renderCellToGfm(state, cell))
    for (let extra = 1; extra < positiveInt(cell.attrs?.colspan); extra += 1) cells.push('')
  }
  state.write(rowLine(cells))
  state.ensureNewLine()
  if (index === 0) {
    state.write(delimiterLine(cells.length))
    state.ensureNewLine()
  }
  const childCount = (parent as ParentLike | null)?.childCount ?? 0
  if (index >= childCount - 1) state.closeBlock(node)
}

/** `tableCell` / `tableHeader` — a bare cell as a slice's top-level node. */
export function serializeTableCell(state: MarkdownState, node: PMNode): void {
  state.write(rowLine([renderCellToGfm(state, node)]))
  state.ensureNewLine()
  state.closeBlock(node)
}
